import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { WarningMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { Tabs } from '@eika/tabs';

import { Content } from '../../components/shared/Content';
import { useConfig } from '../../defaultConfig';
import type { Account, AccountId } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import FuturePaymentsInTransactionList from './FuturePaymentsInTransactionList';
import Transactions from './Transactions';

import './TransactionList.scss';

enum AccountDisplayType {
    TRANSACTION = 'TRANSACTION',
    FUTURE_PAYMENT = 'FUTURE_PAYMENT',
}

interface TransactionTabsProps {
    isLoading?: boolean;
    accountId: AccountId;
    account?: Account;
    accountType?: string;
    setNoAccessToAccountException?: React.Dispatch<React.SetStateAction<RequestExceptionWithResponse | undefined>>;
}

const TransactionTabsSkeleton: React.FC = () => (
    <div className="transaction-list-tabs__wrapper">
        <Skeleton className="transaction-list-tabs__skeleton" />
    </div>
);

const TransactionTabs: React.FC<TransactionTabsProps> = ({
    setNoAccessToAccountException,
    isLoading,
    accountId,
    account,
    accountType,
}) => {
    const translated = useTranslation();
    const { appContext, futurePaymentsListLink } = useConfig();
    const isBusinessUser = appContext === 'BUSINESS';
    if (accountType === 'BSU' || accountType === 'BSU_PLUSS')
        return (
            <Transactions
                setNoAccessToAccountException={setNoAccessToAccountException}
                accountId={accountId}
                account={account}
            />
        );
    if (isLoading) return <TransactionTabsSkeleton />;
    return (
        <Tabs id="display-types" defaultValue={AccountDisplayType.TRANSACTION}>
            <Tabs.TabList>
                <Tabs.TabButton value={AccountDisplayType.TRANSACTION}>{translated.history}</Tabs.TabButton>
                <Tabs.TabButton value={AccountDisplayType.FUTURE_PAYMENT}>
                    {translated.futurePaymentsList.future}
                </Tabs.TabButton>
            </Tabs.TabList>

            <Tabs.Tab value={AccountDisplayType.TRANSACTION}>
                <Transactions
                    setNoAccessToAccountException={setNoAccessToAccountException}
                    accountId={accountId}
                    account={account}
                />
            </Tabs.Tab>
            <Tabs.Tab value={AccountDisplayType.FUTURE_PAYMENT}>
                <FuturePaymentsInTransactionList account={account} />
                {isBusinessUser && (
                    <Content className="future-payments-list__info-message-wrapper">
                        <WarningMessage
                            text={translated.futurePayments.warningBMMissingFilesText}
                            icon={IconTypes.INFO_24}
                            clickableItem={
                                <Link href={futurePaymentsListLink.url}>
                                    {translated.futurePayments.warningBMMissingFilesLink}
                                </Link>
                            }
                        />
                    </Content>
                )}
            </Tabs.Tab>
        </Tabs>
    );
};

export default TransactionTabs;
